import type {
  DashboardPaymentInvoiceTypeGraphicResponse,
  DashboardPaymentInvoiceTypeGraphicApiResponse,
  DashboardPaymentSummaryRequestParams,
  DashboardPaymentSummaryApiResponse,
  DashboardPaymentGraphicRequestParams,
} from '../../types/vida/dashboardPayment/dashboardPaymentType';
import { VIDA_DASHBOARD_PAYMENT_ROUTES } from '../requestsService';

/** Matches BFF `RelatorioPorApoliceRequest` / `ResumoPorApoliceRequest` (field `jsonApoliceSub`, not `apoliceSubList`). */
function buildRelatorioPorApoliceBody(params: {
  tpVisao: string;
  idCorretor?: number;
  idEstipulante?: number;
  apoliceSubList: Array<{ nroApolice: string; cdSubEstipulante: string }>;
}): {
  tpVisao: string;
  idCorretor: number;
  idEstipulante: number;
  jsonApoliceSub: string;
} {
  const list = Array.isArray(params.apoliceSubList) ? params.apoliceSubList : [];
  return {
    tpVisao: params.tpVisao,
    idCorretor: params.idCorretor ?? 0,
    idEstipulante: params.idEstipulante ?? 0,
    jsonApoliceSub: JSON.stringify(
      list.map((x) => ({ nroApolice: x.nroApolice, cdSubEstipulante: x.cdSubEstipulante }))
    ),
  };
}

export class DashboardPaymentService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Gets the dashboard payment invoices graphic data
   * Matches backend endpoint: /v1/bff/vida/dashboard/grafico-faturamento
   */
  async getDashboardPaymentInvoiceTypeGraphic(
    params: DashboardPaymentGraphicRequestParams
  ): Promise<DashboardPaymentInvoiceTypeGraphicApiResponse> {
    const searchParams = new URLSearchParams();
    const url = `${VIDA_DASHBOARD_PAYMENT_ROUTES.GET_DASHBOARD_PAYMENT_INVOICE_GRAPHIC}?${searchParams.toString()}`;

    const body = buildRelatorioPorApoliceBody({
      tpVisao: params.tpVisao ?? '1',
      idCorretor: 0,
      idEstipulante: 0,
      apoliceSubList: Array.isArray(params.apoliceSubList) ? params.apoliceSubList : [],
    });

    const result = await this.fetch<{
      graficoPorMeioDeFaturamentoRetorno?: DashboardPaymentInvoiceTypeGraphicResponse;
      relatorioDownloadRetorno?: { arquivo: string; extensaoArquivo: string } | null;
    }>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    let graphic: DashboardPaymentInvoiceTypeGraphicResponse | null = null;
    if (result && typeof result === 'object' && !Array.isArray(result)) {
      if ('graficoPorMeioDeFaturamentoRetorno' in result && result.graficoPorMeioDeFaturamentoRetorno) {
        graphic = result.graficoPorMeioDeFaturamentoRetorno;
      } else {
        graphic = result as unknown as DashboardPaymentInvoiceTypeGraphicResponse;
      }
    }
    const relatorioDownloadRetorno = result && typeof result === 'object' && 'relatorioDownloadRetorno' in result
      ? (result as { relatorioDownloadRetorno?: { arquivo: string; extensaoArquivo: string } | null }).relatorioDownloadRetorno ?? null
      : null;

    return {
      graphic: graphic as DashboardPaymentInvoiceTypeGraphicResponse,
      relatorioDownloadRetorno: relatorioDownloadRetorno ?? undefined,
    };
  }

  /**
   * Gets the dashboard payment summary for corretora/estipulante view
   * Matches backend endpoint: /v1/bff/vida/dashboard/resumo-corretora-estipulante
   */
  async getDashboardPaymentSummaryCorretoraEstipulante(
    params: DashboardPaymentSummaryRequestParams
  ): Promise<DashboardPaymentSummaryApiResponse> {
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) {
      searchParams.append('page', String(params.page));
    }
    if (params.size !== undefined) {
      searchParams.append('size', String(params.size));
    }

    const url = `${VIDA_DASHBOARD_PAYMENT_ROUTES.GET_DASHBOARD_PAYMENT_CORRETORA_ESTIPULANTE}?${searchParams.toString()}`;

    const body = buildRelatorioPorApoliceBody({
      tpVisao: params.tpVisao ?? '2',
      idCorretor: params.idCorretor,
      idEstipulante: params.idEstipulante,
      apoliceSubList: Array.isArray(params.apoliceSubList) ? params.apoliceSubList : [],
    });

    const result = await this.fetch<DashboardPaymentSummaryApiResponse>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    return result;
  }

  /**
   * Gets the dashboard payment summary for apolice view
   * Matches backend endpoint: /v1/bff/vida/dashboard/resumo-apolice
   */
  async getDashboardPaymentSummaryApolice(
    params: DashboardPaymentSummaryRequestParams
  ): Promise<DashboardPaymentSummaryApiResponse> {
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) {
      searchParams.append('page', String(params.page));
    }
    if (params.size !== undefined) {
      searchParams.append('size', String(params.size));
    }

    const url = `${VIDA_DASHBOARD_PAYMENT_ROUTES.GET_DASHBOARD_PAYMENT_APOLICE}?${searchParams.toString()}`;

    const body = buildRelatorioPorApoliceBody({
      tpVisao: params.tpVisao ?? '1',
      idCorretor: params.idCorretor,
      idEstipulante: params.idEstipulante,
      apoliceSubList: Array.isArray(params.apoliceSubList) ? params.apoliceSubList : [],
    });

    const result = await this.fetch<DashboardPaymentSummaryApiResponse>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    return result;
  }

  /**
   * Gets the dashboard payment summary (wrapper that calls the appropriate endpoint based on view type)
   * Matches backend endpoints: /v1/bff/vida/dashboard/resumo-corretora-estipulante or /v1/bff/vida/dashboard/resumo-apolice
   * tpVisao: "1" = apolice view, "2" = corretora/estipulante view
   */
  async getDashboardPaymentSummary(
    params: DashboardPaymentSummaryRequestParams
  ): Promise<DashboardPaymentSummaryApiResponse> {
    // Use apolice endpoint for tpVisao "1", otherwise use corretora-estipulante endpoint for "2"
    if (params.tpVisao === '1') {
      return this.getDashboardPaymentSummaryApolice(params);
    }
    return this.getDashboardPaymentSummaryCorretoraEstipulante(params);
  }
}

